import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';

interface VanPricing {
  van_tier: string;
  weekly_ex_vat: number;
  setup_ex_vat: number;
  description: string;
  features: string[];
}

interface EligibilityStatus {
  isEligible: boolean;
  activeDays: number;
  incomeVerified: boolean;
  accountStanding: string;
  hasInsurance: boolean;
  hasPaymentMethod: boolean;
}

export default function ProviderVanAccessPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [eligibility, setEligibility] = useState<EligibilityStatus | null>(null);
  const [pricing, setPricing] = useState<VanPricing[]>([]);
  const [selectedTier, setSelectedTier] = useState<string>('');
  const [step, setStep] = useState(1);
  const [insuranceConfirmed, setInsuranceConfirmed] = useState(false);
  const [insuranceDetails, setInsuranceDetails] = useState({
    insurer_name: '',
    policy_number: '',
    policy_expiry_date: ''
  });
  const [deliveryDetails, setDeliveryDetails] = useState({
    delivery_address: '',
    delivery_phone: ''
  });
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/provider/signin');
        return;
      }

      // Load eligibility
      const { data: userData } = await supabase
        .from('users')
        .select('provider_active_days, dashboard_income_verified, account_standing, has_valid_insurance, default_payment_method_id, full_name, phone, address')
        .eq('id', user.id)
        .single();

      if (userData) {
        const eligibilityData: EligibilityStatus = {
          isEligible: 
            (userData.provider_active_days || 0) >= 90 &&
            userData.dashboard_income_verified === true &&
            userData.account_standing === 'good',
          activeDays: userData.provider_active_days || 0,
          incomeVerified: userData.dashboard_income_verified || false,
          accountStanding: userData.account_standing || 'good',
          hasInsurance: userData.has_valid_insurance || false,
          hasPaymentMethod: !!userData.default_payment_method_id
        };
        setEligibility(eligibilityData);

        // Prefill delivery details
        setDeliveryDetails({
          delivery_address: userData.address || '',
          delivery_phone: userData.phone || ''
        });
      }

      // Load pricing
      const { data: pricingData } = await supabase
        .from('van_pricing')
        .select('*')
        .order('weekly_ex_vat', { ascending: true });

      if (pricingData) {
        setPricing(pricingData);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateTotal = (exVat: number) => {
    const vat = exVat * 0.20;
    return exVat + vat;
  };

  const handleSubmitApplication = async () => {
    if (!selectedTier || !insuranceConfirmed) return;

    setProcessing(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const selectedPricing = pricing.find(p => p.van_tier === selectedTier);
      if (!selectedPricing) return;

      // Create application
      const { data: application, error } = await supabase
        .from('van_applications')
        .insert({
          provider_id: user.id,
          van_tier: selectedTier,
          weekly_ex_vat: selectedPricing.weekly_ex_vat,
          setup_ex_vat: selectedPricing.setup_ex_vat,
          status: 'submitted',
          ...insuranceDetails,
          ...deliveryDetails
        })
        .select()
        .single();

      if (error) throw error;

      // Process payment
      const { data: paymentData } = await supabase.functions.invoke('create-van-application-payment', {
        body: {
          application_id: application.id,
          setup_ex_vat: selectedPricing.setup_ex_vat,
          weekly_ex_vat: selectedPricing.weekly_ex_vat
        }
      });

      if (paymentData?.url) {
        window.location.href = paymentData.url;
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      alert('Failed to submit application. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-teal-600 animate-spin"></i>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!eligibility?.isEligible) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-lock-line text-3xl text-gray-400"></i>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Van Access Eligibility</h1>
              <p className="text-gray-600">Complete these requirements to unlock van access</p>
            </div>

            <div className="space-y-4">
              <div className={`flex items-center gap-3 p-4 rounded-lg ${eligibility && eligibility.activeDays >= 90 ? 'bg-green-50' : 'bg-gray-50'}`}>
                <i className={`ri-${eligibility && eligibility.activeDays >= 90 ? 'checkbox-circle' : 'checkbox-blank-circle'}-line text-2xl ${eligibility && eligibility.activeDays >= 90 ? 'text-green-600' : 'text-gray-400'}`}></i>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">On platform 3+ months</p>
                  <p className="text-sm text-gray-600">{eligibility?.activeDays || 0} days active {eligibility && eligibility.activeDays < 90 && `(${90 - eligibility.activeDays} days remaining)`}</p>
                </div>
              </div>

              <div className={`flex items-center gap-3 p-4 rounded-lg ${eligibility?.incomeVerified ? 'bg-green-50' : 'bg-gray-50'}`}>
                <i className={`ri-${eligibility?.incomeVerified ? 'checkbox-circle' : 'checkbox-blank-circle'}-line text-2xl ${eligibility?.incomeVerified ? 'text-green-600' : 'text-gray-400'}`}></i>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Proven income on Taskbudy</p>
                  <p className="text-sm text-gray-600">Complete jobs to verify earnings</p>
                </div>
              </div>

              <div className={`flex items-center gap-3 p-4 rounded-lg ${eligibility?.accountStanding === 'good' ? 'bg-green-50' : 'bg-gray-50'}`}>
                <i className={`ri-${eligibility?.accountStanding === 'good' ? 'checkbox-circle' : 'checkbox-blank-circle'}-line text-2xl ${eligibility?.accountStanding === 'good' ? 'text-green-600' : 'text-gray-400'}`}></i>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Good standing</p>
                  <p className="text-sm text-gray-600">No major disputes or blocks</p>
                </div>
              </div>

              <div className={`flex items-center gap-3 p-4 rounded-lg bg-gray-50`}>
                <i className="ri-checkbox-blank-circle-line text-2xl text-gray-400"></i>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Insurance ready</p>
                  <p className="text-sm text-gray-600">Will be collected during application</p>
                </div>
              </div>

              <div className={`flex items-center gap-3 p-4 rounded-lg bg-gray-50`}>
                <i className="ri-checkbox-blank-circle-line text-2xl text-gray-400"></i>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Payment method set up</p>
                  <p className="text-sm text-gray-600">Will be collected during application</p>
                </div>
              </div>
            </div>

            <button
              onClick={() => navigate('/provider/dashboard')}
              className="w-full mt-8 px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white font-medium rounded-lg transition-colors whitespace-nowrap"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-center gap-4">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${step >= s ? 'bg-teal-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                  {s}
                </div>
                {s < 3 && <div className={`w-16 h-1 ${step > s ? 'bg-teal-600' : 'bg-gray-200'}`}></div>}
              </div>
            ))}
          </div>
          <div className="flex justify-center gap-16 mt-4">
            <p className={`text-sm ${step >= 1 ? 'text-teal-600 font-medium' : 'text-gray-500'}`}>Select Van</p>
            <p className={`text-sm ${step >= 2 ? 'text-teal-600 font-medium' : 'text-gray-500'}`}>Insurance</p>
            <p className={`text-sm ${step >= 3 ? 'text-teal-600 font-medium' : 'text-gray-500'}`}>Payment</p>
          </div>
        </div>

        {/* Step 1: Select Van Size */}
        {step === 1 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Select Van Size</h2>
            <p className="text-gray-600 mb-8">Choose the van that best fits your business needs</p>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              {pricing.map((tier) => (
                <div
                  key={tier.van_tier}
                  onClick={() => setSelectedTier(tier.van_tier)}
                  className={`border-2 rounded-xl p-6 cursor-pointer transition-all ${selectedTier === tier.van_tier ? 'border-teal-600 bg-teal-50' : 'border-gray-200 hover:border-teal-300'}`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-gray-900 capitalize">{tier.van_tier} Van</h3>
                    {selectedTier === tier.van_tier && (
                      <i className="ri-checkbox-circle-fill text-2xl text-teal-600"></i>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mb-4">{tier.description}</p>
                  
                  <div className="space-y-2 mb-4">
                    <div>
                      <p className="text-sm text-gray-500">Weekly</p>
                      <p className="text-xl font-bold text-gray-900">£{tier.weekly_ex_vat.toFixed(2)} <span className="text-sm font-normal text-gray-500">ex VAT</span></p>
                      <p className="text-sm text-gray-600">£{calculateTotal(tier.weekly_ex_vat).toFixed(2)} inc VAT</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Setup Cost</p>
                      <p className="text-lg font-semibold text-gray-900">£{tier.setup_ex_vat.toFixed(2)} <span className="text-sm font-normal text-gray-500">ex VAT</span></p>
                      <p className="text-sm text-gray-600">£{calculateTotal(tier.setup_ex_vat).toFixed(2)} inc VAT</p>
                    </div>
                  </div>

                  <ul className="space-y-2">
                    {tier.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                        <i className="ri-check-line text-teal-600"></i>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <div className="flex gap-3">
                <i className="ri-information-line text-amber-600 text-xl flex-shrink-0"></i>
                <div className="text-sm text-amber-900">
                  <p className="font-medium mb-1">Important Information</p>
                  <ul className="space-y-1 text-amber-800">
                    <li>• Vans are allocated or sourced by Taskbudy. Vehicle make/model/year may vary.</li>
                    <li>• Weekly billing starts on delivery.</li>
                    <li>• Initial setup cost is not a deposit.</li>
                    <li>• Vans remain Taskbudy property.</li>
                    <li>• If payments stop, Taskbudy will collect the van and reassign it.</li>
                    <li>• No credit checks required.</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex gap-4">
              <button
                onClick={() => navigate('/provider/dashboard')}
                className="px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={() => setStep(2)}
                disabled={!selectedTier}
                className="flex-1 px-6 py-3 bg-teal-600 hover:bg-teal-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors whitespace-nowrap"
              >
                Continue to Insurance
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Insurance & Details */}
        {step === 2 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Insurance & Delivery Details</h2>
            <p className="text-gray-600 mb-8">Provide your insurance information and delivery address</p>

            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={insuranceConfirmed}
                    onChange={(e) => setInsuranceConfirmed(e.target.checked)}
                    className="mt-1 w-5 h-5 text-teal-600 rounded focus:ring-teal-500"
                  />
                  <span className="text-sm text-blue-900 font-medium">
                    I confirm I have valid insurance to drive this van
                  </span>
                </label>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Insurer Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={insuranceDetails.insurer_name}
                    onChange={(e) => setInsuranceDetails({...insuranceDetails, insurer_name: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="e.g., Aviva, Direct Line"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Policy Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={insuranceDetails.policy_number}
                    onChange={(e) => setInsuranceDetails({...insuranceDetails, policy_number: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="Policy number"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Policy Expiry Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={insuranceDetails.policy_expiry_date}
                    onChange={(e) => setInsuranceDetails({...insuranceDetails, policy_expiry_date: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                </div>
              </div>

              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Delivery Details</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Delivery Address <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={deliveryDetails.delivery_address}
                      onChange={(e) => setDeliveryDetails({...deliveryDetails, delivery_address: e.target.value})}
                      rows={3}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="Full delivery address"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Contact Phone <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      value={deliveryDetails.delivery_phone}
                      onChange={(e) => setDeliveryDetails({...deliveryDetails, delivery_phone: e.target.value})}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="Phone number"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-4 mt-8">
              <button
                onClick={() => setStep(1)}
                className="px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
              >
                Back
              </button>
              <button
                onClick={() => setStep(3)}
                disabled={!insuranceConfirmed || !insuranceDetails.insurer_name || !insuranceDetails.policy_number || !insuranceDetails.policy_expiry_date || !deliveryDetails.delivery_address || !deliveryDetails.delivery_phone}
                className="flex-1 px-6 py-3 bg-teal-600 hover:bg-teal-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors whitespace-nowrap"
              >
                Continue to Payment
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Payment */}
        {step === 3 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Upfront Payment</h2>
            <p className="text-gray-600 mb-8">Pay setup cost and first week to complete your application</p>

            {selectedTier && pricing.find(p => p.van_tier === selectedTier) && (
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <h3 className="font-semibold text-gray-900 mb-4 capitalize">{selectedTier} Van - Payment Summary</h3>
                
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Setup Cost (ex VAT)</span>
                    <span className="font-medium text-gray-900">£{pricing.find(p => p.van_tier === selectedTier)!.setup_ex_vat.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">First Week (ex VAT)</span>
                    <span className="font-medium text-gray-900">£{pricing.find(p => p.van_tier === selectedTier)!.weekly_ex_vat.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">VAT (20%)</span>
                    <span className="font-medium text-gray-900">£{((pricing.find(p => p.van_tier === selectedTier)!.setup_ex_vat + pricing.find(p => p.van_tier === selectedTier)!.weekly_ex_vat) * 0.20).toFixed(2)}</span>
                  </div>
                  <div className="border-t pt-3 flex justify-between">
                    <span className="font-semibold text-gray-900">Total Due Today</span>
                    <span className="text-xl font-bold text-teal-600">£{calculateTotal(pricing.find(p => p.van_tier === selectedTier)!.setup_ex_vat + pricing.find(p => p.van_tier === selectedTier)!.weekly_ex_vat).toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex gap-3">
                <i className="ri-information-line text-blue-600 text-xl flex-shrink-0"></i>
                <div className="text-sm text-blue-900">
                  <p className="font-medium mb-2">Payment Terms</p>
                  <ul className="space-y-1 text-blue-800">
                    <li>• <strong>Weekly billing starts on delivery</strong> - You won't be charged weekly until your van is delivered</li>
                    <li>• <strong>Setup cost is not a deposit</strong> - This is a non-refundable activation fee</li>
                    <li>• <strong>First week credit applied</strong> - Your first week payment will be credited once the van is delivered</li>
                    <li>• <strong>Vans remain Taskbudy property</strong> - You're renting, not purchasing</li>
                    <li>• <strong>If payments stop, Taskbudy will collect the van and reassign it</strong></li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex gap-4">
              <button
                onClick={() => setStep(2)}
                disabled={processing}
                className="px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap disabled:opacity-50"
              >
                Back
              </button>
              <button
                onClick={handleSubmitApplication}
                disabled={processing}
                className="flex-1 px-6 py-3 bg-teal-600 hover:bg-teal-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
              >
                {processing ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Processing...
                  </>
                ) : (
                  <>
                    <i className="ri-secure-payment-line"></i>
                    Pay & Submit Application
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
